import React from 'react';
import { useButtonLinks } from '../hooks/useButtonLinks';
import { useTranslation } from '../hooks/useTranslation';

const ProAccount = () => {
  const { handleButtonClick } = useButtonLinks();
  const { t } = useTranslation();
  return (
    <div className="pro-account-page">
      {/* Hero Section */}
      <section className="pro-hero">
        <div className="container">
          <div className="pro-hero-content">
            <h1 className="pro-title">{t('proAccount.hero.title')}</h1>
            <p className="pro-subtitle">
              {t('proAccount.hero.subtitle')}
            </p>
            <div className="hero-question">
              <h2>{t('proAccount.hero.question')}</h2>
            </div>
            <div className="pro-features">
              <div className="feature-highlight">
                <i className="fas fa-star"></i>
                <span>{t('proAccount.hero.features.platform')}</span>
              </div>
              <div className="feature-highlight">
                <i className="fas fa-users"></i>
                <span>{t('proAccount.hero.features.partners')}</span>
              </div>
              <div className="feature-highlight">
                <i className="fas fa-tools"></i>
                <span>{t('proAccount.hero.features.tools')}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What You'll Get Section */}
      <section className="pro-benefits">
        <div className="container">
          <div className="benefits-header">
            <h2 className="section-title">{t('proAccount.benefits.title')}</h2>
            <p className="section-subtitle">{t('proAccount.benefits.subtitle')}</p>
          </div>
          
          <div className="benefits-grid">
            <div className="benefit-item">
              <i className="fas fa-chart-line"></i>
              <p>{t('proAccount.benefits.items.spreads')}</p>
            </div>
            <div className="benefit-item">
              <i className="fas fa-globe"></i>
              <p>{t('proAccount.benefits.items.instruments')}</p>
            </div>
            <div className="benefit-item">
              <i className="fas fa-mobile-alt"></i>
              <p>{t('proAccount.benefits.items.platform')}</p>
            </div>
            <div className="benefit-item">
              <i className="fas fa-bolt"></i>
              <p>{t('proAccount.benefits.items.deposits')}</p>
            </div>
            <div className="benefit-item">
              <i className="fas fa-chart-bar"></i>
              <p>{t('proAccount.benefits.items.charting')}</p>
            </div>
            <div className="benefit-item">
              <i className="fas fa-graduation-cap"></i>
              <p>{t('proAccount.benefits.items.education')}</p>
            </div>
            <div className="benefit-item">
              <i className="fas fa-headset"></i>
              <p>{t('proAccount.benefits.items.support')}</p>
            </div>
            <div className="benefit-item">
              <i className="fas fa-database"></i>
              <p>{t('proAccount.benefits.items.data')}</p>
            </div>
            <div className="benefit-item">
              <i className="fas fa-shield-alt"></i>
              <p>{t('proAccount.benefits.items.protection')}</p>
            </div>
          </div>

          <div className="benefits-cta">
            <p className="cta-text">{t('proAccount.benefits.cta')}</p>
            <button 
              className="btn btn-primary"
              onClick={(e) => handleButtonClick('getStarted', e)}
            >
              {t('proAccount.benefits.button')}
            </button>
          </div>
        </div>
      </section>

      {/* Specifications Section */}
      <section className="pro-specifications">
        <div className="container">
          <h2 className="section-title">{t('proAccount.specifications.title')}</h2>
          <div className="specs-grid">
            <div className="spec-card">
              <div className="spec-item">
                <span className="spec-label">{t('accountShared.specifications.spread')}</span>
                <span className="spec-value">{t('proAccount.specifications.spread')}</span>
              </div>
              <div className="spec-item">
                <span className="spec-label">{t('accountShared.specifications.demoAccount')}</span>
                <span className="spec-value">{t('proAccount.specifications.demoAccount')}</span>
              </div>
              <div className="spec-item">
                <span className="spec-label">{t('accountShared.specifications.leverage')}</span>
                <span className="spec-value">{t('proAccount.specifications.leverage')}</span>
              </div>
              <div className="spec-item">
                <span className="spec-label">{t('accountShared.specifications.economicCalendar')}</span>
                <span className="spec-value">{t('proAccount.specifications.economicCalendar')}</span>
              </div>
              <div className="spec-item">
                <span className="spec-label">{t('accountShared.specifications.commission')}</span>
                <span className="spec-value">{t('proAccount.specifications.commission')}</span>
              </div>
              <div className="spec-item">
                <span className="spec-label">{t('accountShared.specifications.instruments')}</span>
                <span className="spec-value">{t('proAccount.specifications.instruments')}</span>
              </div>
            </div>

            <div className="spec-card">
              <div className="spec-item">
                <span className="spec-label">{t('accountShared.specifications.markets')}</span>
                <span className="spec-value">{t('proAccount.specifications.markets')}</span>
              </div>
              <div className="spec-item">
                <span className="spec-label">{t('accountShared.specifications.expertAdvisory')}</span>
                <span className="spec-value">{t('proAccount.specifications.expertAdvisory')}</span>
              </div>
              <div className="spec-item">
                <span className="spec-label">{t('accountShared.specifications.minOrder')}</span>
                <span className="spec-value">{t('proAccount.specifications.minOrder')}</span>
              </div>
              <div className="spec-item">
                <span className="spec-label">{t('accountShared.specifications.technicalAnalysis')}</span>
                <span className="spec-value">{t('proAccount.specifications.technicalAnalysis')}</span>
              </div>
              <div className="spec-item">
                <span className="spec-label">{t('accountShared.specifications.minDeposit')}</span>
                <span className="spec-value">{t('proAccount.specifications.minDeposit')}</span>
              </div>
              <div className="spec-item">
                <span className="spec-label">{t('accountShared.specifications.liveNews')}</span>
                <span className="spec-value">{t('proAccount.specifications.liveNews')}</span>
              </div>
            </div>

            <div className="spec-card">
              <div className="spec-item">
                <span className="spec-label">{t('accountShared.specifications.maxPositions')}</span>
                <span className="spec-value">{t('proAccount.specifications.maxPositions')}</span>
              </div>
              <div className="spec-item">
                <span className="spec-label">{t('accountShared.specifications.freeData')}</span>
                <span className="spec-value">{t('proAccount.specifications.freeData')}</span>
              </div>
              <div className="spec-item">
                <span className="spec-label">{t('accountShared.specifications.swap')}</span>
                <span className="spec-value">{t('proAccount.specifications.swap')}</span>
              </div>
              <div className="spec-item">
                <span className="spec-label">{t('accountShared.specifications.phoneSupport')}</span>
                <span className="spec-value">{t('proAccount.specifications.phoneSupport')}</span>
              </div>
              <div className="spec-item">
                <span className="spec-label">{t('accountShared.specifications.stopOut')}</span>
                <span className="spec-value">{t('proAccount.specifications.stopOut')}</span>
              </div>
              <div className="spec-item">
                <span className="spec-label">{t('accountShared.specifications.educationalVideos')}</span>
                <span className="spec-value">{t('proAccount.specifications.educationalVideos')}</span>
              </div>
            </div>

            <div className="spec-card">
              <div className="spec-item">
                <span className="spec-label">{t('accountShared.specifications.chartingPackages')}</span>
                <span className="spec-value">{t('proAccount.specifications.chartingPackages')}</span>
              </div>
              <div className="spec-item">
                <span className="spec-label">{t('accountShared.specifications.seminars')}</span>
                <span className="spec-value">{t('proAccount.specifications.seminars')}</span>
              </div>
              <div className="spec-item">
                <span className="spec-label">{t('accountShared.specifications.platform')}</span>
                <span className="spec-value">{t('proAccount.specifications.platform')}</span>
              </div>
              <div className="spec-item">
                <span className="spec-label">{t('accountShared.specifications.newsArticles')}</span>
                <span className="spec-value">{t('proAccount.specifications.newsArticles')}</span>
              </div>
            </div>
          </div>
          <p className="specs-note">{t('proAccount.specifications.note')}</p>
        </div>
      </section>

      {/* Risk Warning Section */}
      <section className="risk-warning">
        <div className="container">
          <h2 className="section-title">{t('proAccount.riskWarning.title')}</h2>
          <div className="warning-content">
            <div className="warning-header">
              <h3>{t('proAccount.riskWarning.excited')}</h3>
              <p>{t('proAccount.riskWarning.subtitle')}</p>
            </div>
            <div className="warning-points">
              <div className="warning-item">
                <i className="fas fa-exclamation-triangle"></i>
                <p>{t('proAccount.riskWarning.warnings.risk')}</p>
              </div>
              <div className="warning-item">
                <i className="fas fa-shield-alt"></i>
                <p>{t('proAccount.riskWarning.warnings.management')}</p>
              </div>
              <div className="warning-item">
                <i className="fas fa-lightbulb"></i>
                <p>{t('proAccount.riskWarning.warnings.demo')}</p>
              </div>
              <div className="warning-item">
                <i className="fas fa-clock"></i>
                <p>{t('proAccount.riskWarning.warnings.time')}</p>
              </div>
            </div>
            <div className="warning-cta">
              <button 
                className="btn btn-primary"
                onClick={(e) => handleButtonClick('openAccount', e)}
              >
                {t('proAccount.riskWarning.createButton')}
              </button>
              <button className="btn btn-secondary">{t('proAccount.riskWarning.tourButton')}</button>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Help Section */}
      <section className="quick-help">
        <div className="container">
          <h2 className="section-title">{t('accountShared.quickHelp.title')}</h2>
          <div className="help-sections">
            <div className="help-column">
              <h3>{t('accountShared.quickHelp.experienced.title')}</h3>
              <div className="help-items">
                <div className="help-item">
                  <h4>{t('accountShared.quickHelp.experienced.partner.title')}</h4>
                  <p>{t('accountShared.quickHelp.experienced.partner.description')}</p>
                </div>
                <div className="help-item">
                  <h4>{t('accountShared.quickHelp.experienced.markets.title')}</h4>
                  <p>{t('accountShared.quickHelp.experienced.markets.description')}</p>
                </div>
                <div className="help-item">
                  <h4>{t('accountShared.quickHelp.experienced.events.title')}</h4>
                  <p>{t('accountShared.quickHelp.experienced.events.description')}</p>
                </div>
              </div>
            </div>
            
            <div className="help-column">
              <h3>{t('accountShared.quickHelp.newTrader.title')}</h3>
              <div className="help-items">
                <div className="help-item">
                  <h4>{t('accountShared.quickHelp.newTrader.platform.title')}</h4>
                  <p>{t('accountShared.quickHelp.newTrader.platform.description')}</p>
                </div>
                <div className="help-item">
                  <h4>{t('accountShared.quickHelp.newTrader.charts.title')}</h4>
                  <p>{t('accountShared.quickHelp.newTrader.charts.description')}</p>
                </div>
                <div className="help-item">
                  <h4>{t('accountShared.quickHelp.newTrader.demo.title')}</h4>
                  <p>{t('accountShared.quickHelp.newTrader.demo.description')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProAccount;